import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Card, Loading, Button } from '../common';
import { currencyService } from '../../services/currencyService';
import {
  ArrowPathIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const QuickRateUpdate = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [editedRates, setEditedRates] = useState({});

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [currenciesRes, ratesRes] = await Promise.all([
        currencyService.getCurrencies(true),
        currencyService.getExchangeRates()
      ]);
      const currenciesData = currenciesRes.success ? currenciesRes.data : [];
      const ratesData = ratesRes.success ? ratesRes.data : [];
      setCurrencies(currenciesData);
      setExchangeRates(ratesData);

      // Initialize edited rates with current values
      const initialEdited = {};
      ratesData.forEach(rate => {
        initialEdited[rate.id] = {
          buyRate: parseFloat(rate.buyRate),
          sellRate: parseFloat(rate.sellRate)
        };
      });
      setEditedRates(initialEdited);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('currencies.fetchRatesError') || 'Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  };

  // Get currency code by ID
  const getCurrencyCode = useCallback((id) => {
    const currency = currencies.find(c => String(c.id) === String(id));
    return currency?.code || 'N/A';
  }, [currencies]);

  // Adjust rate by increment
  const adjustRate = (rateId, field, increment) => {
    setEditedRates(prev => {
      const current = prev[rateId]?.[field] || 0;
      // Use appropriate step based on the rate value
      const step = current > 100 ? 1 : 0.01;
      const newValue = Math.max(0, current + (increment * step));
      return {
        ...prev,
        [rateId]: {
          ...prev[rateId],
          [field]: parseFloat(newValue.toFixed(4))
        }
      };
    });
  };

  // Handle direct input change
  const handleInputChange = (rateId, field, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedRates(prev => ({
      ...prev,
      [rateId]: {
        ...prev[rateId],
        [field]: numValue
      }
    }));
  };

  // Check if rate has been modified
  const isModified = (rateId) => {
    const original = exchangeRates.find(r => r.id === rateId);
    const edited = editedRates[rateId];
    if (!original || !edited) return false;
    return (
      parseFloat(original.buyRate) !== edited.buyRate ||
      parseFloat(original.sellRate) !== edited.sellRate
    );
  };

  // Save a single rate
  const saveRate = async (rate) => {
    const edited = editedRates[rate.id];
    if (!edited) return;

    try {
      setSaving(true);
      await currencyService.setExchangeRate({
        fromCurrencyId: rate.fromCurrency?.id,
        toCurrencyId: rate.toCurrency?.id,
        buyRate: edited.buyRate,
        sellRate: edited.sellRate
      });
      toast.success(t('currencies.rateSet') || 'Rate updated successfully');
      // Refresh to get the latest data
      await fetchData();
    } catch (error) {
      console.error('Failed to save rate:', error);
      toast.error(error.response?.data?.message || t('currencies.setRateError') || 'Failed to update rate');
    } finally {
      setSaving(false);
    }
  };

  // Filter to show main currency pairs (USD/IQD first)
  const getMainRates = () => {
    return [...exchangeRates].sort((a, b) => {
      // USD/IQD always first
      const aIsUsdIqd = a.fromCurrency?.code === 'USD' && a.toCurrency?.code === 'IQD';
      const bIsUsdIqd = b.fromCurrency?.code === 'USD' && b.toCurrency?.code === 'IQD';
      if (aIsUsdIqd && !bIsUsdIqd) return -1;
      if (!aIsUsdIqd && bIsUsdIqd) return 1;
      // Then other X/IQD pairs
      const aToIqd = a.toCurrency?.code === 'IQD';
      const bToIqd = b.toCurrency?.code === 'IQD';
      if (aToIqd && !bToIqd) return -1;
      if (!aToIqd && bToIqd) return 1;
      return 0;
    }).slice(0, 4);
  };

  if (loading) {
    return (
      <Card title={t('dashboard.quickRateUpdate.title') || 'Quick Rate Update'}>
        <div className="flex items-center justify-center h-32">
          <Loading size="md" />
        </div>
      </Card>
    );
  }

  const mainRates = getMainRates();

  if (mainRates.length === 0) {
    return (
      <Card
        title={t('dashboard.quickRateUpdate.title') || 'Quick Rate Update'}
        action={
          <CurrencyDollarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        }
      >
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {t('dashboard.quickRateUpdate.noRates') || 'No exchange rates configured'}
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={t('dashboard.quickRateUpdate.title') || 'Quick Rate Update'}
      action={
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={t('common.refresh') || 'Refresh'}
        >
          <ArrowPathIcon className={`h-5 w-5 text-gray-500 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className="space-y-4">
        {mainRates.map((rate) => {
          const fromCode = rate.fromCurrency?.code || "N/A";
          const toCode = rate.toCurrency?.code || "N/A";
          const edited = editedRates[rate.id] || { buyRate: 0, sellRate: 0 };
          const modified = isModified(rate.id);

          return (
            <div
              key={rate.id}
              className={`p-4 rounded-lg border transition-colors ${
                modified
                  ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              {/* Currency Pair Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {fromCode}/{toCode}
                  </span>
                  {modified && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300">
                      {t('common.modified') || 'Modified'}
                    </span>
                  )}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => saveRate(rate)}
                  disabled={!modified || saving}
                  className="!py-1 !px-3"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  {t('common.save') || 'Save'}
                </Button>
              </div>

              {/* Rate Inputs */}
              <div className="grid grid-cols-2 gap-4">
                {/* Buy Rate */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {t('currencies.buyRate') || 'Buy Rate'}
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => adjustRate(rate.id, 'buyRate', -1)}
                      className="p-2 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <MinusIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <input
                      type="number"
                      value={edited.buyRate}
                      onChange={(e) => handleInputChange(rate.id, 'buyRate', e.target.value)}
                      className="flex-1 min-w-0 px-2 py-2 text-center border-y border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      step="any"
                    />
                    <button
                      type="button"
                      onClick={() => adjustRate(rate.id, 'buyRate', 1)}
                      className="p-2 rounded-r-md border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Sell Rate */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {t('currencies.sellRate') || 'Sell Rate'}
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => adjustRate(rate.id, 'sellRate', -1)}
                      className="p-2 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <MinusIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <input
                      type="number"
                      value={edited.sellRate}
                      onChange={(e) => handleInputChange(rate.id, 'sellRate', e.target.value)}
                      className="flex-1 min-w-0 px-2 py-2 text-center border-y border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      step="any"
                    />
                    <button
                      type="button"
                      onClick={() => adjustRate(rate.id, 'sellRate', 1)}
                      className="p-2 rounded-r-md border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default QuickRateUpdate;
